import type { ScriptReplaceMark } from "@/services/make-generate";

/** 规范化到半开区间 [a,b)：外部存储使用闭区间 [start,end] */
function normalizeHalfOpenRange(range: [number, number]): [number, number] {
  let [a, b] = range;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return [0, 0];
  if (a > b) [a, b] = [b, a];
  const na = Math.max(0, Math.floor(a));
  const nb = Math.max(0, Math.floor(b));
  return [na, nb + 1];
}

/** 存储区间：统一输出闭区间 [start,end]（含 end） */
function toStoredRange(range: [number, number]): [number, number] {
  const [a, b] = range;
  return [a, b - 1];
}

/** 两段区间是否相交（半开区间） */
function rangesOverlap(a: [number, number], b: [number, number]): boolean {
  return a[0] < b[1] && b[0] < a[1];
}

/** 按 indexRange 的左端点排序 */
function sortMarks(marks: ScriptReplaceMark[]): ScriptReplaceMark[] {
  return [...marks].sort((x, y) => x.indexRange[0] - y.indexRange[0]);
}

/** 相邻重复字符时，多位置删除会得到同一 next；用删除后光标在 next 中的偏移消歧 */
function singleDeleteIndex(prev: string, next: string, caretAfter?: number): number | null {
  if (prev.length !== next.length + 1) return null;
  const candidates: number[] = [];
  for (let d = 0; d < prev.length; d++) {
    if (prev.slice(0, d) + prev.slice(d + 1) === next) candidates.push(d);
  }
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  if (caretAfter === undefined) return null;

  const pick = (fn: (d: number) => boolean) => {
    const hit = candidates.filter(fn);
    return hit.length === 1 ? hit[0] : null;
  };

  const a = pick((d) => d === caretAfter);
  if (a !== null) return a;
  const b = pick((d) => d === caretAfter + 1);
  if (b !== null) return b;
  const c = pick((d) => d === caretAfter - 1);
  if (c !== null) return c;

  candidates.sort((x, y) => Math.abs(x - caretAfter) - Math.abs(y - caretAfter));
  return candidates[0] ?? null;
}

/** 相邻重复字符时，多位置插入会得到同一 prev；用插入后光标在 next 中的偏移消歧 */
function singleInsertIndex(prev: string, next: string, caretAfter?: number): number | null {
  if (next.length !== prev.length + 1) return null;
  const candidates: number[] = [];
  for (let ins = 0; ins < next.length; ins++) {
    if (prev === next.slice(0, ins) + next.slice(ins + 1)) candidates.push(ins);
  }
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  if (caretAfter === undefined) return null;

  const pick = (fn: (ins: number) => boolean) => {
    const hit = candidates.filter(fn);
    return hit.length === 1 ? hit[0] : null;
  };

  const a = pick((ins) => ins + 1 === caretAfter);
  if (a !== null) return a;
  const b = pick((ins) => ins === caretAfter);
  if (b !== null) return b;

  candidates.sort((x, y) => Math.abs(x + 1 - caretAfter) - Math.abs(y + 1 - caretAfter));
  return candidates[0] ?? null;
}

/**
 * 单次删除：删掉 prev 中下标 d 的那一个字符。
 * 若 d 落在某条 mark 的半开区间 [a,b) 内，则整条读音标记作废（不再保留收缩后的区间）。
 */
function adjustMarksAfterSingleDelete(d: number, marks: ScriptReplaceMark[]): ScriptReplaceMark[] {
  const out: ScriptReplaceMark[] = [];
  for (const m of marks) {
    const [a, b] = normalizeHalfOpenRange(m.indexRange);
    if (b <= a) continue;
    if (a <= d && d < b) {
      continue;
    }
    if (b <= d) {
      out.push({ ...m, type: "replace", indexRange: toStoredRange([a, b]) });
    } else {
      out.push({ ...m, type: "replace", indexRange: toStoredRange([a - 1, b - 1]) });
    }
  }
  return sortMarks(out.filter((m) => {
    const [a, b] = normalizeHalfOpenRange(m.indexRange);
    return b > a;
  }));
}

/** 
 * 单次插入：在 prev 中下标 ins 的位置插入一个字符。
 * 若 ins 落在某条 mark 的半开区间 [a,b) 内，则整条读音标记作废（不再保留扩张后的区间）。
 */
function adjustMarksAfterSingleInsert(ins: number, marks: ScriptReplaceMark[]): ScriptReplaceMark[] {
  const out: ScriptReplaceMark[] = [];
  for (const m of marks) {
    const [a, b] = normalizeHalfOpenRange(m.indexRange);
    if (b <= a) continue;
    // 半开 [a,b)：next[ins] 为插入字符。ins<=a 插在首字之前（含紧贴左边界），标记整体右移；a<ins<b 为段内扩大右端；ins>=b 段后不变。
    let na: number;
    let nb: number;
    if (ins <= a) {
      na = a + 1;
      nb = b + 1;
    } else if (ins < b) {
      na = a;
      nb = b + 1;
    } else {
      na = a;
      nb = b;
    }
    if (nb > na) out.push({ ...m, type: "replace", indexRange: toStoredRange([na, nb]) });
  }
  return sortMarks(out);
}

/**
 * 在纯文本从 prev 变为 next 时同步 marks：优先按单次插入/删除平移或收缩标记；否则按前后缀编辑区回退策略
 */
function reconcileScriptReplaceMarksByPrefixSuffix(
  prev: string,
  next: string,
  marks: ScriptReplaceMark[]
): ScriptReplaceMark[] {
  let i = 0;
  const minLen = Math.min(prev.length, next.length);
  while (i < minLen && prev[i] === next[i]) i++;
  let j = 0;
  while (j < minLen - i && prev[prev.length - 1 - j] === next[next.length - 1 - j]) j++;

  const editStart = i;
  const editEndOld = prev.length - j;
  const delta = next.length - prev.length;

  const out: ScriptReplaceMark[] = [];
  for (const m of marks) {
    const [a, b] = normalizeHalfOpenRange(m.indexRange);
    if (b <= a || a > prev.length) continue;
    if (b <= editStart) {
      out.push({ ...m, type: "replace", indexRange: toStoredRange([a, b]) });
    } else if (a >= editEndOld) {
      const na = a + delta;
      const nb = b + delta;
      if (na >= 0 && nb <= next.length && nb > na) {
        out.push({ ...m, type: "replace", indexRange: toStoredRange([na, nb]) });
      }
    }
  }
  return sortMarks(out);
}

/**
 * 查询与区间完全一致的 replace 标记的读音，无则返回空串
 */
export function getScriptReplaceReadingForRange(
  marks: ScriptReplaceMark[],
  indexRange: [number, number]
): string {
  const nr = normalizeHalfOpenRange(indexRange);
  const hit = marks.find((m) => {
    const r = normalizeHalfOpenRange(m.indexRange);
    return r[0] === nr[0] && r[1] === nr[1];
  });
  return hit?.text ?? "";
}

/**
 * 移除与给定区间完全一致的 replace 标记（外部存储为闭区间）
 */
export function removeScriptReplaceMark(
  marks: ScriptReplaceMark[],
  indexRange: [number, number]
): ScriptReplaceMark[] {
  const nr = normalizeHalfOpenRange(indexRange);
  if (nr[1] <= nr[0]) return marks;
  return marks.filter((m) => {
    const r = normalizeHalfOpenRange(m.indexRange);
    return !(r[0] === nr[0] && r[1] === nr[1]);
  });
}

/**
 * 写入或更新一条 replace 标记：与新区间相交的旧标记会被移除（划词重叠则覆盖）
 */
export function upsertScriptReplaceMark(marks: ScriptReplaceMark[], next: ScriptReplaceMark): ScriptReplaceMark[] {
  const nr = normalizeHalfOpenRange(next.indexRange);
  if (nr[1] <= nr[0]) return marks;
  const merged: ScriptReplaceMark = { type: "replace", indexRange: toStoredRange(nr), text: next.text };
  const kept = marks.filter((m) => {
    const r = normalizeHalfOpenRange(m.indexRange);
    return !rangesOverlap(r, nr);
  });
  return sortMarks([...kept, merged]);
}

/**
 * 在纯文本从 prev 变为 next 时同步 marks。
 * `caretAfter`：变更后、在 **next** 字符串坐标系下的光标偏移（用于相邻重复字时的单次插入/删除消歧）。
 */
export function reconcileScriptReplaceMarks(
  prev: string,
  next: string,
  marks: ScriptReplaceMark[],
  caretAfter?: number
): ScriptReplaceMark[] {
  if (!marks.length || prev === next) return marks;

  const del = singleDeleteIndex(prev, next, caretAfter);
  if (del !== null) {
    return adjustMarksAfterSingleDelete(del, marks);
  }

  const ins = singleInsertIndex(prev, next, caretAfter);
  if (ins !== null) {
    return adjustMarksAfterSingleInsert(ins, marks);
  }

  return reconcileScriptReplaceMarksByPrefixSuffix(prev, next, marks);
}
